import React, { useState, useEffect, useMemo } from "react";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { GridApi, ColDef, GridReadyEvent } from "ag-grid-community";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import CancelIcon from "@mui/icons-material/Cancel";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

interface SafeAgGridProps extends AgGridReactProps {
  rowData: any[];
  columnDefs: ColDef[];
  enableQuickSearch?: boolean;
  enableGrouping?: boolean;
  defaultAutoGroupColumnDef?: ColDef;
  refreshIntervalSeconds?: number;
  loading?: boolean;
  groupLocked?: boolean;
  errorFallbackMessage?: string;
}

const SafeAgGrid: React.FC<SafeAgGridProps> = ({
  rowData,
  columnDefs,
  enableQuickSearch = true,
  enableGrouping = true,
  defaultAutoGroupColumnDef,
  refreshIntervalSeconds = 120,
  loading = false,
  groupLocked = false,
  errorFallbackMessage = "An error occurred while loading the table.",
  ...agGridProps
}) => {
  const theme = useTheme();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [grouping, setGrouping] = useState<string[]>([]);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(30);
  const [refreshTimer, setRefreshTimer] = useState<NodeJS.Timeout | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validColumnDefs = useMemo(() => columnDefs.filter(c => c && c.headerName), [columnDefs]);

  const handleGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
  };

  useEffect(() => {
    if (!refreshIntervalSeconds || !gridApi || refreshIntervalSeconds < 30) return;
    const timer = setInterval(() => {
      try {
        gridApi.refreshClientSideRowModel("everything");
      } catch (e) {
        setError("Auto-refresh failed.");
        console.error("Auto-refresh error:", e);
      }
    }, refreshIntervalSeconds * 1000);
    setRefreshTimer(timer);
    return () => clearInterval(timer);
  }, [gridApi, refreshIntervalSeconds]);

  const handleQuickSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleRefresh = () => {
    try {
      gridApi?.refreshClientSideRowModel("everything");
    } catch (e) {
      console.error("Manual refresh error:", e);
      setError("Manual refresh failed.");
    }
  };

  const handleGroupByField = (field: string) => {
    if (!gridApi) return;
    const newGrouping = grouping.includes(field)
      ? grouping
      : grouping.length < 2
        ? [...grouping, field]
        : [grouping[1], field];

    gridApi.applyColumnState({
      state: newGrouping.map(f => ({ colId: f, rowGroup: true })),
      defaultState: { rowGroup: false },
    });
    setGrouping(newGrouping);
    setGroupMenuAnchor(null);
  };

  const handleClearGrouping = () => {
    gridApi?.applyColumnState({ defaultState: { rowGroup: false } });
    setGrouping([]);
    setGroupMenuAnchor(null);
  };

  const hasData = rowData?.length > 0;
  const hasColumns = validColumnDefs.length > 0;

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{errorFallbackMessage}</Typography>
        <Typography variant="body2" mt={1}>{error}</Typography>
      </Box>
    );
  }

  if (!hasColumns) {
    return <Typography sx={{ p: 2 }}>No columns defined. Please configure the table.</Typography>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={2}>
        <Box display="flex" gap={1} alignItems="center">
          {enableQuickSearch && (
            <TextField
              size="small"
              value={searchText}
              placeholder="Quick Search..."
              onChange={handleQuickSearch}
              aria-label="Quick search"
              sx={{ minWidth: 200 }}
            />
          )}
          <Tooltip title="Refresh Now">
            <IconButton onClick={handleRefresh} aria-label="Refresh">
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {enableGrouping && !groupLocked && (
            <>
              <Tooltip title="Group By Field">
                <IconButton onClick={(e) => setGroupMenuAnchor(e.currentTarget)} aria-label="Group">
                  <GroupWorkIcon />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={groupMenuAnchor} open={!!groupMenuAnchor} onClose={() => setGroupMenuAnchor(null)}>
                {validColumnDefs.map((col) => (
                  <MenuItem key={col.field} onClick={() => handleGroupByField(col.field!)}>
                    Group by {col.headerName || col.field}
                  </MenuItem>
                ))}
                {grouping.length > 0 && (
                  <MenuItem onClick={handleClearGrouping}>
                    <ClearAllIcon fontSize="small" sx={{ mr: 1 }} /> Clear Grouping
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>

        {grouping.length > 0 && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle2">Grouped by: {grouping.join(", ")}</Typography>
            <IconButton size="small" onClick={handleClearGrouping} aria-label="Clear grouping">
              <CancelIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <FormControl size="small">
          <InputLabel id="page-size-label">Rows</InputLabel>
          <Select
            labelId="page-size-label"
            value={pageSize}
            label="Rows"
            onChange={(e) => {
              const size = parseInt(e.target.value as string, 10);
              setPageSize(size);
              gridApi?.setGridOption('paginationPageSize', size);
            }}
          >
            {[20, 30, 50, 100].map(size => (
              <MenuItem key={size} value={size}>{size} / page</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress aria-label="Loading data" />
        </Box>
      ) : (
        <Box
          className={`ag-theme-${theme.palette.mode === "dark" ? "alpine-dark" : "alpine"}`}
          sx={{
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            minHeight: 300,
            "& .ag-root-wrapper": { borderRadius: 2 },
            "& .ag-header-cell-label": { fontWeight: 600 },
            "& .ag-row": { fontSize: "0.9rem", minHeight: "48px !important" },
          }}
        >
          <AgGridReact
            {...agGridProps}
            rowData={rowData}
            columnDefs={validColumnDefs}
            onGridReady={handleGridReady}
            domLayout="autoHeight"
            suppressColumnVirtualisation
            enableRangeSelection
            animateRows
            groupDisplayType="multipleColumns"
            groupDefaultExpanded={-1}
            pagination
            paginationPageSize={pageSize}
            quickFilterText={searchText}
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
              flex: 1,
              floatingFilter: false,
              ...(agGridProps.defaultColDef || {}),
            }}
            autoGroupColumnDef={defaultAutoGroupColumnDef || {
              headerName: "Group",
              pinned: "left",
              suppressMovable: true,
              minWidth: 200,
              cellRendererParams: { suppressCount: false }
            }}
            rowSelection={(agGridProps.rowSelection || "multiple")}
          />
        </Box>
      )}
    </Box>
  );
};

export default SafeAgGrid;
